// Service Worker 更新の適用を、重い初期化 (useInitializeCWebCatalog) の完了後に遅延する。
// registerType: "prompt" + SW 側の SKIP_WAITING 待ちと対になる。
// autoUpdate + 即時 skipWaiting だと install 完了直後の reload が初期化／Search と衝突し、
// Circle/Search が Status (unknown) になる
//
// 本プラグインが初期化ハング等で動かない場合の安全網:
//   pwaUpdateTimeoutFallback.client.ts（enforce:"pre" / waiting → N 秒後に無条件 SKIP_WAITING）
// SKIP_WAITING を送れない配下（旧 autoUpdate バンドル等）の移行:
//   app/service-worker/sw.ts が install 時の ping 無応答を検知して自己 skipWaiting する

// 不変条件: MAX_WAIT_MS + POST_IDLE_SETTLE_MS < pwaUpdateTimeoutFallback の
// FALLBACK_SKIP_WAITING_MS(200s)。逆転すると安全網が通常パスより先に発火し、
// 正当に継続中のブートストラップへ reload が割り込む。値を変える際は両方を確認する。
const MAX_WAIT_MS = 180_000;
const BOOTSTRAP_GRACE_MS = 10_000;
/** bootstrap / refresh 完了後、一覧の初回 Search が走り切る余裕（本番 Search p99 は数十 ms オーダー） */
const POST_IDLE_SETTLE_MS = 3_000;

const sleep = (ms: number) => new Promise<void>((resolve) => {
  setTimeout(resolve, ms);
});

export default defineNuxtPlugin({
  name: "pwa-update-defer",
  enforce: "post",
  setup() {
    // dev では更新適用リロード（SKIP_WAITING → controlling → reload）を行わない。
    // dev の SW 更新検知でリロードが起動フロー（Cookie2Token 等）へ割り込むのを防ぐ。
    if (import.meta.dev) {
      return;
    }
    const nuxtApp = useNuxtApp();
    const pwa = nuxtApp.$pwa;
    if (!pwa) {
      return;
    }

    let applyingUpdate = false;
    let activatingFirstInstall = false;

    // 初期化シングルトン（provide 済み・安定参照）。inflight/refreshInflight は可変プロパティを都度読む。
    const store = nuxtApp.$cWebCatalogInitStore;

    /**
     * inflight / refreshInflight があれば完了を待ってから BOOTSTRAP_GRACE_MS 分の
     * 追い掛け余裕(inflight → refreshInflight などの後発 pending 吸収)を確保する。
     * pending が長引いても MAX_WAIT_MS で必ず打ち切る。
     * ループを使わないので tight-loop の余地なし (#33449)。
     */
    const waitUntilBootstrapIdle = async () => {
      const deadline = sleep(MAX_WAIT_MS);
      const pending = store.inflight ?? store.refreshInflight;
      if (pending) {
        await Promise.race([pending.catch(() => undefined), deadline]);
      }
      // 先の pending 完了後に refreshInflight が新たに立ち上がる可能性に備え、
      // grace 分だけ後発 pending を追い掛ける (grace 中に出現しなければそのまま抜ける)。
      await Promise.race([sleep(BOOTSTRAP_GRACE_MS), deadline]);
      const followUp = store.inflight ?? store.refreshInflight;
      if (followUp && followUp !== pending) {
        await Promise.race([followUp.catch(() => undefined), deadline]);
      }
    };

    /** 更新あり（旧 SW 制御中に新 SW が installed）。初期化 idle 後に適用して reload する。 */
    const applyUpdateWhenIdle = async () => {
      if (applyingUpdate) {
        return;
      }
      applyingUpdate = true;
      try {
        await waitUntilBootstrapIdle();
        // start() 完了直後の searchCircle と reload が重ならないよう短く待つ
        await sleep(POST_IDLE_SETTLE_MS);
        // 待機 SW があれば SKIP_WAITING → controlling で reload（無ければ no-op）
        await pwa.updateServiceWorker(true);
      }
      finally {
        applyingUpdate = false;
      }
    };

    /** 初回 install（controller 無し）は reload せず SKIP_WAITING のみ（次回ナビから SW 制御）。 */
    const activateFirstInstall = async () => {
      if (activatingFirstInstall) {
        return;
      }
      activatingFirstInstall = true;
      try {
        await pwa.updateServiceWorker(false);
      }
      finally {
        activatingFirstInstall = false;
      }
    };

    // vite-pwa の登録完了フックで registration を受け取り、SW ライフサイクルイベントで
    // 更新 / 初回 install を検出する（Vue watch は使わない）。
    // installed 時点で controller があれば更新（needRefresh 相当）、無ければ初回 install（offlineReady 相当）。
    nuxtApp.hook("service-worker:registered", ({ registration }) => {
      if (!registration) {
        return;
      }
      // 登録時点で既に waiting（前回ロードで install 済みの更新）がある場合。
      if (registration.waiting && navigator.serviceWorker.controller) {
        void applyUpdateWhenIdle();
      }
      registration.addEventListener("updatefound", () => {
        const installing = registration.installing;
        if (!installing) {
          return;
        }
        installing.addEventListener("statechange", () => {
          if (installing.state !== "installed") {
            return;
          }
          if (navigator.serviceWorker.controller) {
            void applyUpdateWhenIdle();
          }
          else {
            void activateFirstInstall();
          }
        });
      });
    });
  },
});
